import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Linkedin, Mail, Send } from "lucide-react";
import { FADE_UP_30, VISIBLE, VIEWPORT, TITLE_TRANSITION } from "@/lib/motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT_INFO = {
  linkedin: "https://www.linkedin.com/in/himanshukumaroberoi",
  email: "connect@digitalcentaur.com",
};

const INITIAL_FORM = { name: "", email: "", company: "", message: "" };

function useContactForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Message received. I'll be in touch shortly.");
      setForm(INITIAL_FORM);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return { form, loading, onChange, submit };
}

const ContactChannels = () => (
  <div className="col-span-12 md:col-span-4 border border-stroke bg-matte" data-testid="contact-channels">
    <a href={CONTACT_INFO.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin"
      className="group flex items-center justify-between p-5 md:p-6 border-b border-stroke hover:bg-obsidian/60 transition-colors">
      <div className="flex items-center gap-4">
        <Linkedin size={20} strokeWidth={1.5} className="text-laser" />
        <div>
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-steel">LinkedIn</div>
          <div className="font-sans text-ink text-sm md:text-base">/himanshukumaroberoi</div>
        </div>
      </div>
      <ArrowUpRight size={18} strokeWidth={1.5} className="text-steel group-hover:text-laser transition-colors" />
    </a>
    <a href={`mailto:${CONTACT_INFO.email}`} data-testid="contact-email"
      className="group flex items-center justify-between p-5 md:p-6 border-b border-stroke hover:bg-obsidian/60 transition-colors">
      <div className="flex items-center gap-4">
        <Mail size={20} strokeWidth={1.5} className="text-laser" />
        <div>
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-steel">Email</div>
          <div className="font-sans text-ink text-sm md:text-base">{CONTACT_INFO.email}</div>
        </div>
      </div>
      <ArrowUpRight size={18} strokeWidth={1.5} className="text-steel group-hover:text-laser transition-colors" />
    </a>
    <div className="p-5 md:p-6">
      <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-laser">Located</div>
      <div className="mt-2 font-serif text-ink text-2xl leading-tight">Dubai, UAE</div>
      <div className="mt-1 font-mono text-[11px] tracking-[0.2em] uppercase text-steel">GMT+4</div>
    </div>
  </div>
);

const FIELD_CLASS =
  "w-full bg-transparent border border-stroke px-4 py-3 text-ink font-sans placeholder:text-steel/50 focus:border-laser focus:outline-none transition-colors";

const ContactForm = ({ form, loading, onChange, submit }) => (
  <form onSubmit={submit} className="col-span-12 md:col-span-8 border border-stroke bg-matte p-6 md:p-10" data-testid="contact-form">
    <div className="grid grid-cols-12 gap-4 md:gap-6">
      <div className="col-span-12 md:col-span-6">
        <label htmlFor="name" className="block font-mono text-[10px] tracking-[0.28em] uppercase text-steel mb-2">Name</label>
        <input id="name" name="name" type="text" autoComplete="name" value={form.name} onChange={onChange} required data-testid="contact-input-name"
          className={FIELD_CLASS} placeholder="Your full name" />
      </div>
      <div className="col-span-12 md:col-span-6">
        <label htmlFor="email" className="block font-mono text-[10px] tracking-[0.28em] uppercase text-steel mb-2">Email</label>
        <input id="email" name="email" type="email" autoComplete="email" value={form.email} onChange={onChange} required data-testid="contact-input-email"
          className={FIELD_CLASS} placeholder="you@company.com" />
      </div>
      <div className="col-span-12">
        <label htmlFor="company" className="block font-mono text-[10px] tracking-[0.28em] uppercase text-steel mb-2">
          Company <span className="text-steel/50 normal-case tracking-normal">(optional)</span>
        </label>
        <input id="company" name="company" type="text" autoComplete="organization" value={form.company} onChange={onChange} data-testid="contact-input-company"
          className={FIELD_CLASS} placeholder="Where you're calling from" />
      </div>
      <div className="col-span-12">
        <label htmlFor="message" className="block font-mono text-[10px] tracking-[0.28em] uppercase text-steel mb-2">Message</label>
        <textarea id="message" name="message" rows={6} value={form.message} onChange={onChange} required data-testid="contact-input-message"
          className={`${FIELD_CLASS} resize-none`} placeholder="Tell me about the mandate, timeline and geography." />
      </div>
      <div className="col-span-12 flex items-center justify-end pt-2">
        <button type="submit" disabled={loading} data-testid="contact-submit-button"
          className="btn-sweep btn-sweep-primary group inline-flex items-center gap-3 border border-laser text-ink px-6 py-4 font-mono text-xs tracking-[0.24em] uppercase disabled:opacity-60 disabled:cursor-not-allowed">
          {loading ? (<>Transmitting…</>) : (<><Send size={16} strokeWidth={1.5} />Send Message</>)}
        </button>
      </div>
    </div>
  </form>
);

export default function Contact() {
  const { form, loading, onChange, submit } = useContactForm();

  return (
    <section id="contact" className="relative py-24 md:py-40" data-testid="contact-section">
      <div className="container-x relative z-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-4">
            <p className="eyebrow">Contact</p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <motion.h2
              initial={FADE_UP_30}
              whileInView={VISIBLE}
              viewport={VIEWPORT}
              transition={TITLE_TRANSITION}
              className="font-serif font-light text-[13vw] md:text-[9vw] lg:text-[8vw] leading-[0.9] tracking-tighter text-ink"
              data-testid="contact-title"
            >
              LET&rsquo;S <span className="text-laser italic">talk.</span>
            </motion.h2>
            <p className="mt-6 max-w-xl font-sans text-lg text-steel leading-relaxed">
              Growth mandate, consulting engagement, or a fintech launch. I&rsquo;ll respond within a working day.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <ContactChannels />
          <ContactForm form={form} loading={loading} onChange={onChange} submit={submit} />
        </div>
      </div>
    </section>
  );
}
